<template>
  <div>
    <label
      :class="`${isDragging ? 'bg-primary/5' : 'bg-zinc-50'} flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded border border-dashed border-primary p-4 transition-colors`"
      for="upload-image"
      @drop.prevent="handleDrop"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
    >
      <Icon class="text-2xl text-primary" name="backup" is-filled />

      <span class="mt-1 block text-center text-sm font-medium text-primary">
        {{ __('Upload photo') }}
      </span>
      <span
        class="mt-2.5 block text-center text-xs leading-normal text-zinc-600"
      >
        {{ __('Click to upload or drag and drop') }}
      </span>
      <span class="mt-1 block text-center text-xs leading-normal text-zinc-600">
        {{ `JPG, JPEG ${__('or')} PNG (max. 2 MB)` }}
      </span>
    </label>

    <div v-if="errors.length" class="mt-2 space-y-1">
      <span
        v-for="error in errors"
        :key="error"
        class="block text-xs text-error"
      >
        {{ error }}
      </span>
    </div>

    <input
      id="upload-image"
      class="sr-only"
      type="file"
      :accept="extensions.join(',')"
      @change="handleInputChange"
    />

    <ImageCropper
      v-model:open="isCropperOpen"
      :value="base64"
      @change="handleCropperChange"
    />
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { __ } from '@/helpers';
  import { Icon } from '@/components/block/icon';
  import { ImageCropper } from '@/components/other/image-cropper';

  const props = defineProps({
    errors: {
      type: Array,
      default: () => []
    }
  });

  const emits = defineEmits(['change', 'update:errors']);

  const maxFileSize = 2;
  const extensions = ['.jpg', '.jpeg', '.png'];

  const base64 = ref('');
  const isCropperOpen = ref(false);
  const isDragging = ref(false);

  function readFiles(files) {
    if (!files || !files.length) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      base64.value = reader.result;
      isCropperOpen.value = true;
    };

    reader.readAsDataURL(files[0]);
  }

  function handleInputChange(event) {
    readFiles(event.target.files);
    event.target.value = '';
  }

  function handleDrop(event) {
    const files = Array.from(event.dataTransfer.files);

    const validFiles = files.filter((file) =>
      extensions
        .map((extension) => `image/${extension.split('.')[1]}`)
        .includes(file.type)
    );

    if (validFiles.length) {
      readFiles(validFiles);
    }

    isDragging.value = false;
  }

  function handleDragOver() {
    isDragging.value = true;
  }

  function handleDragLeave() {
    isDragging.value = false;
  }

  function handleCropperChange(base64, file) {
    if (file.size > maxFileSize * 1024 * 1024) {
      emits('update:errors', [
        `${__('File size exceeds limit. Please upload a file smaller than')} ${maxFileSize} MB.`
      ]);
      return;
    }

    emits('change', base64, file);
    emits('update:errors', []);
  }
</script>
